import { Component, computed, effect, EventEmitter, input, Input, Output, inject } from '@angular/core';
import { IBackcastingChartOut, IChartOptions, IChartPointValue } from 'src/models/charts.model';
import { ECurrencyCode } from 'src/models/common.model';
import { EWeeksBetweenInvestments } from 'src/models/products.model';
import { AREA_FILL_BELOW_LINE } from 'src/util/charts.config';
import { PercentPipe } from '@angular/common';

@Component({
  selector: 'liberro-backcasting-chart',
  standalone: false,
  templateUrl: './liberro-backcasting-chart.component.html',
  styleUrl: './liberro-backcasting-chart.component.less'
})
export class LiberroBackcastingChartComponent {
  private percentPipe = inject(PercentPipe);

  @Input({ required: true }) public currencyCode!: ECurrencyCode;
  public chartData = input.required<IBackcastingChartOut>();
  public hasErrored = input.required<boolean>();
  public noDataToDisplay = computed(() => !this.chartData() || !this.chartData().graph.length);
  public chartOptions?: IChartOptions;

  @Output() public updateBackcastinChart = new EventEmitter<EWeeksBetweenInvestments>();

  public constructor() {
    effect(() => {
      const chartData = this.chartData();
      if (chartData) {
        this.updateChart(chartData.graph);
      }
    });
  }

  private updateChart(chartData: IChartPointValue[]): void {
    const min = Math.min(...chartData.map(p => p.y));
    const max = Math.max(...chartData.map(p => p.y));

    this.chartOptions = {
      series: [{
        name: '',
        data: chartData.map(d => ({ x: d.x, y: d.y }))
      }],
      legend: {
        show: false,
      },
      markers: {
        size: 0,
        strokeOpacity: 0,
        strokeWidth: 0,
        hover: {
          size: 0,
          sizeOffset: 0
        },
      },
      chart: {
        type: 'area',
        height: 264,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      dataLabels: {
        enabled: false
      },
      plotOptions: AREA_FILL_BELOW_LINE,
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: ['#3D8146'],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.5,
          opacityTo: 0,
          stops: [0, 100],
          colorStops: [
            {
              offset: 0,
              color: '#3D8146',
              opacity: 0.5
            },
            {
              offset: 100,
              color: '#3D8146',
              opacity: 0
            }
          ]
        }
      },
      grid: {
        show: false,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: false } }
      },
      xaxis: {
        type: 'numeric',
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
        crosshairs: { show: false }
      },
      yaxis: {
        tooltip: { enabled: true },
        opposite: true,
        crosshairs: {
          show: true,
          stroke: {
            width: 1,
            dashArray: 5,
            color: '#3D8146'
          }
        },
        labels: {
          offsetX: -12,
          formatter: (value) => this.percentPipe.transform(value, '1.0') ?? '',
          style: {
            colors: '#6F9BA1',
            fontSize: '11px'
          }
        },
        min: min,
        max: max,
        tickAmount: 7,
      },
      tooltip: {
        enabled: true,
        marker: {
          show: false,
        },
        custom: () => {
          return ''; // hide default tooltip
        }
      },
    };
  }

}
